import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, DatePicker, Empty, Input, Row, Space, Table, Tag, Tooltip, Typography } from "antd";
import { ExclamationCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { firestore } from "../../../configs/FirebaseConfig";
import { FIRESTORE_EDI_LOG_COLLECTION, FIRESTORE_REGIONS_TABLE } from "../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import { StyledSelect } from "../../../styles/sharedStyles";
import useIsMobile from "../../../hooks/useIsMobile";
import { EDI_EVENTS, EDI_MESSAGE_TYPES, EDI_SOURCES } from "./constants";
import { EdiMessageTypeTag, EdiStatusTag } from "./EdiLogTags";
import EdiLogDrawer from "./EdiLogDrawer";

dayjs.extend(relativeTime);

const { RangePicker } = DatePicker;
const { Link, Text } = Typography;

const DEFAULT_DAYS = 7;
const defaultRange = () => [
  dayjs()
    .subtract(DEFAULT_DAYS - 1, "day")
    .startOf("day"),
  dayjs().endOf("day"),
];

const EdiLog = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(defaultRange);
  const [searchTerm, setSearchTerm] = useState("");
  const [messageType, setMessageType] = useState("all");
  const [event, setEvent] = useState("all");
  const [source, setSource] = useState("all");
  const [status, setStatus] = useState("all");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // ?edifactOrderNumber=… (link vanuit de order-drawer): alle logs van die
  // order, ongeacht de datum.
  const [searchParams, setSearchParams] = useSearchParams();
  const orderFilter = searchParams.get("edifactOrderNumber");
  const setOrderFilter = (value) => setSearchParams(value ? { edifactOrderNumber: value } : {});

  // Live op datum (één veld: geen composite index nodig); de overige filters
  // gebeuren hieronder in het geheugen.
  useEffect(() => {
    const [start, end] = dateRange || defaultRange();
    setLoading(true);
    const logCollection = collection(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_EDI_LOG_COLLECTION,
    );
    // Op ordernummer zonder orderBy: één veld, dus geen composite index. De
    // tabel sorteert zelf op tijd.
    const logQuery = orderFilter
      ? query(logCollection, where("edifactOrderNumber", "==", orderFilter))
      : query(
          logCollection,
          where("createdAt", ">=", start.startOf("day").unix()),
          where("createdAt", "<=", end.endOf("day").unix()),
          orderBy("createdAt", "desc"),
        );
    return onSnapshot(
      logQuery,
      (snapshot) => {
        setEntries(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching EDI log:", error);
        setLoading(false);
      },
    );
  }, [dateRange, orderFilter]);

  const filteredEntries = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return entries.filter(
      (entry) =>
        (messageType === "all" || entry.messageType === messageType) &&
        (event === "all" || entry.event === event) &&
        (source === "all" || entry.source === source) &&
        (status === "all" || entry.status === status) &&
        (!term ||
          [entry.edifactOrderNumber, entry.farmorderId, entry.error, entry.id].some((value) =>
            value?.toLowerCase().includes(term),
          )),
    );
  }, [entries, searchTerm, messageType, event, source, status]);

  const errorCount = filteredEntries.filter((entry) => entry.status === "error").length;
  const hasActiveFilters =
    !!orderFilter || !!searchTerm || messageType !== "all" || event !== "all" || source !== "all" || status !== "all";

  const clearFilters = () => {
    setOrderFilter(null);
    setSearchTerm("");
    setMessageType("all");
    setEvent("all");
    setSource("all");
    setStatus("all");
  };

  const showDrawer = (entry) => {
    setSelectedEntry(entry);
    setDrawerOpen(true);
  };

  const columns = [
    {
      title: t("edi_log_time"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (createdAt) => (
        <Space direction="vertical" size={0}>
          <Text strong>{dayjs.unix(createdAt).format("DD-MM-YYYY HH:mm:ss")}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs.unix(createdAt).fromNow()}
          </Text>
        </Space>
      ),
      sorter: (a, b) => a.createdAt - b.createdAt,
      defaultSortOrder: "descend",
    },
    {
      title: t("edi_log_message_type"),
      key: "messageType",
      width: 120,
      render: (_, entry) => <EdiMessageTypeTag entry={entry} />,
    },
    {
      title: t("edi_log_event"),
      dataIndex: "event",
      key: "event",
      width: 130,
      render: (value) => t(`edi_log_event_${value}`),
    },
    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (value, entry) => (
        <Space size={4}>
          <EdiStatusTag status={value} />
          {entry.error && (
            <Tooltip title={entry.error}>
              <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: t("edi_log_source"),
      dataIndex: "source",
      key: "source",
      width: 110,
      responsive: ["lg"],
      render: (value) => t(`edi_log_source_${value}`),
    },
    {
      title: t("edi_log_order_number"),
      dataIndex: "edifactOrderNumber",
      key: "edifactOrderNumber",
      width: 150,
      render: (value) =>
        value ? (
          <Link
            onClick={(e) => {
              e.stopPropagation();
              setOrderFilter(value);
            }}
          >
            {value}
          </Link>
        ) : (
          "-"
        ),
    },
    {
      title: t("edi_log_farmorder"),
      dataIndex: "farmorderId",
      key: "farmorderId",
      width: 150,
      responsive: ["lg"],
      render: (value) => value || "-",
    },
  ];

  const filterSelect = (value, onChange, allLabel, options, labelPrefix, width = 150) => (
    <StyledSelect
      value={value}
      onChange={onChange}
      style={{ width: isMobile ? "100%" : width }}
      $isFiltered={value !== "all"}
      options={[
        { value: "all", label: allLabel },
        ...options.map((option) => ({ value: option, label: labelPrefix ? t(`${labelPrefix}${option}`) : option })),
      ]}
    />
  );

  return (
    <Row gutter={16}>
      <Col xs={24}>
        <h1>{t("edi_log")}</h1>
        <Text type="secondary">{t("edi_log_subtitle")}</Text>
      </Col>
      <Col xs={24} style={{ marginTop: 16 }}>
        <Card>
          <Space wrap direction={isMobile ? "vertical" : "horizontal"} style={{ width: "100%", marginBottom: 16 }}>
            <Input
              placeholder={t("edi_log_search_placeholder")}
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              style={{ width: isMobile ? "100%" : 260 }}
            />
            <RangePicker
              value={dateRange}
              onChange={(range) => setDateRange(range || defaultRange())}
              format="DD-MM-YYYY"
              allowClear={false}
              disabled={!!orderFilter}
              placeholder={[t("start_date"), t("end_date")]}
              style={isMobile ? { width: "100%" } : undefined}
            />
            {filterSelect(messageType, setMessageType, t("edi_log_all_types"), EDI_MESSAGE_TYPES)}
            {filterSelect(event, setEvent, t("edi_log_all_events"), EDI_EVENTS, "edi_log_event_")}
            {filterSelect(source, setSource, t("edi_log_all_sources"), EDI_SOURCES, "edi_log_source_")}
            {filterSelect(status, setStatus, t("edi_log_all_statuses"), ["success", "error"], "edi_log_status_")}
            {hasActiveFilters && <Button onClick={clearFilters}>{t("edi_log_clear_filters")}</Button>}
          </Space>

          <Space style={{ marginBottom: 16 }}>
            {orderFilter && (
              <Tag closable onClose={() => setOrderFilter(null)} color="blue">
                {t("edi_log_filtered_on_order", { orderNumber: orderFilter })}
              </Tag>
            )}
            <Text type="secondary">
              {t("edi_log_showing", { count: filteredEntries.length, total: entries.length })}
            </Text>
            {errorCount > 0 && (
              <Link onClick={() => setStatus("error")}>
                <Badge count={errorCount} title={t("edi_log_status_error")} />
              </Link>
            )}
          </Space>

          <div className="table-responsive">
            <Table
              showSorterTooltip={false}
              loading={loading}
              columns={columns}
              dataSource={filteredEntries}
              rowKey="id"
              size="middle"
              locale={{ emptyText: <Empty description={t("edi_log_no_entries")} /> }}
              pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: (total) => `${t("total")} ${total} ${t("items")}`,
              }}
              onRow={(entry) => ({ onClick: () => showDrawer(entry), style: { cursor: "pointer" } })}
            />
          </div>
        </Card>
      </Col>

      <EdiLogDrawer
        entry={selectedEntry}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSelect={setSelectedEntry}
        isMobile={isMobile}
      />
    </Row>
  );
};

export default EdiLog;
